<template>
  <div class="w-full">
    <h2>{{ t('handoverDone') }}</h2>
    <!-- External drivers only finish photo uploads here; no booking access (ADR 0021). -->
    <template v-if="isExternalDriver">
      <StatusBanner v-if="pendingUploads > 0" variant="warning" class="mt-4">
        {{ t('keepOpen', { count: pendingUploads }, pendingUploads) }}
      </StatusBanner>
      <template v-else>
        <BigSuccess />
        <p class="mt-6 text-center">{{ t('allUploaded') }}</p>
      </template>
    </template>
    <template v-else>
      <p class="mt-4">
        {{ t('vehicleCanBeHandedOver', { bookingNumber: booking?.bookingNumber }) }}
      </p>
      <BigSuccess />

      <Divider />
      <div class="flex justify-end gap-4">
        <CVButton
          variant="secondary"
          :to="{ name: 'bookingEdit', params: { id: route.params.id } }"
        >
          {{ t('showBooking') }}
        </CVButton>
        <CVButton :to="{ name: 'upcomingHandovers' }">
          {{ t('next') }}
        </CVButton>
      </div>
    </template>
  </div>
</template>
<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import BigSuccess from '@/components/BigSuccess.vue';
import Divider from '@/components/Divider.vue';
import { useRoute } from 'vue-router';
import { useBooking } from '@/queries/use-bookings';
import { computed } from 'vue';
import { useExternalDriver } from '@/hooks/use-external-driver';
import StatusBanner from '@/components/StatusBanner.vue';
import { useEvidenceUploadQueue } from '@/hooks/use-evidence-upload-queue';

const { t } = useI18n();
const route = useRoute();
const { isExternalDriver } = useExternalDriver();
const { items: queuedUploads } = useEvidenceUploadQueue();
const pendingUploads = computed(() => queuedUploads.value.length);
const { data: booking } = useBooking(
  route.params.id as string,
  computed(() => !isExternalDriver.value),
);
</script>

<i18n lang="json">
{
  "en": {
    "keepOpen": "One photo is still uploading. Please keep this page open until it is done. | {count} photos are still uploading. Please keep this page open until all are done.",
    "allUploaded": "All photos are uploaded. Thank you, you can close this page now.",
    "handoverDone": "Handover Done",
    "next": "Back to Dashboard",
    "showBooking": "Show Booking",
    "vehicleCanBeHandedOver": "Booking {bookingNumber} can now be handed over."
  },
  "de": {
    "keepOpen": "Ein Foto wird noch hochgeladen. Bitte lass diese Seite geöffnet, bis es fertig ist. | {count} Fotos werden noch hochgeladen. Bitte lass diese Seite geöffnet, bis alle fertig sind.",
    "allUploaded": "Alle Fotos sind hochgeladen. Danke, du kannst diese Seite jetzt schließen.",
    "handoverDone": "Übergabe abgeschlossen",
    "next": "Zurück zur Übersicht",
    "showBooking": "Buchung anzeigen",
    "vehicleCanBeHandedOver": "Buchung {bookingNumber} kann jetzt übergeben werden."
  },
  "es": {
    "keepOpen": "Todavía se está subiendo una foto. Deja esta página abierta hasta que termine. | Todavía se están subiendo {count} fotos. Deja esta página abierta hasta que terminen todas.",
    "allUploaded": "Todas las fotos están subidas. Gracias, ya puedes cerrar esta página.",
    "handoverDone": "Entrega completada",
    "next": "Volver a la vista general",
    "showBooking": "Ver reserva",
    "vehicleCanBeHandedOver": "La reserva {bookingNumber} ya puede entregarse."
  }
}
</i18n>
